/**
 * Venttra - Data Tools view for CMS
 * Batch operations for destinations, guides, and hierarchy fixes.
 */
import React, { useCallback, useState } from "react";
import { useAuthController, useSnackbarController } from "@firecms/core";
import { Button, Paper, Typography } from "@firecms/ui";
import { BatchDelayField } from "./data-tools/BatchDelayField";
import { DestinationDataTools } from "./data-tools/DestinationDataTools";
import { GuideDataTools } from "./data-tools/GuideDataTools";

const CONTENT_ENGINE_URL = import.meta.env.VITE_CONTENT_ENGINE_URL;

type DataToolsTab = "destinations" | "guides";

export function DataToolsView() {
  const authController = useAuthController();
  const snackbar = useSnackbarController();
  const baseUrl = CONTENT_ENGINE_URL?.replace(/\/$/, "");
  const [tab, setTab] = useState<DataToolsTab>("destinations");
  const [batchDelaySec, setBatchDelaySec] = useState(3);
  const [seedingSite, setSeedingSite] = useState(false);
  const [translatingChrome, setTranslatingChrome] = useState(false);
  const [translatingPageCopy, setTranslatingPageCopy] = useState(false);

  const getToken = useCallback(async () => {
    const token = await authController.getAuthToken();
    if (!token?.trim()) {
      snackbar.open({
        type: "error",
        message: "Not authenticated. Please sign out and sign in again.",
      });
      return null;
    }
    return token;
  }, [authController, snackbar]);

  const seedSiteContent = useCallback(async () => {
    if (!baseUrl) return;
    const token = await getToken();
    if (!token) return;
    setSeedingSite(true);
    try {
      const res = await fetch(`${baseUrl}/seed-site-content`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ force: false }),
      });
      const data = (await res.json()) as {
        created?: string[];
        skipped?: string[];
        filled?: string[];
        error?: string;
      };
      if (!res.ok) {
        snackbar.open({ type: "error", message: data.error ?? "Seed site content failed" });
        return;
      }
      snackbar.open({
        type: "success",
        message: `Site content seed: created ${(data.created ?? []).length}, filled ${(data.filled ?? []).length}, skipped ${(data.skipped ?? []).length}.`,
      });
    } catch (err) {
      snackbar.open({
        type: "error",
        message: err instanceof Error ? err.message : "Seed site content failed",
      });
    } finally {
      setSeedingSite(false);
    }
  }, [baseUrl, getToken, snackbar]);

  const runSiteTranslate = useCallback(
    async (
      endpoint: "/translate-site-chrome" | "/translate-page-copy",
      documentId: string,
      mode: "force" | "fill_empty" | "stale_only",
      setBusy: (v: boolean) => void,
    ) => {
      if (!baseUrl) return;
      const token = await getToken();
      if (!token) return;
      setBusy(true);
      try {
        const res = await fetch(`${baseUrl}${endpoint}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ document_id: documentId, mode }),
        });
        const data = (await res.json()) as {
          ok?: boolean;
          message?: string;
          error?: string;
          skipped?: boolean;
        };
        if (res.ok && data.ok) {
          snackbar.open({
            type: data.skipped ? "info" : "success",
            message: data.message ?? "Translation complete",
          });
        } else {
          snackbar.open({ type: "error", message: data.error ?? "Translation failed" });
        }
      } catch (err) {
        snackbar.open({
          type: "error",
          message: err instanceof Error ? err.message : "Translation failed",
        });
      } finally {
        setBusy(false);
      }
    },
    [baseUrl, getToken, snackbar],
  );

  if (!baseUrl) {
    return (
      <div className="p-8">
        <Typography variant="h5">Data Tools</Typography>
        <Paper className="p-4 mt-4">
          <Typography color="error">
            Content Engine URL not configured. Set VITE_CONTENT_ENGINE_URL in .env
          </Typography>
        </Paper>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      <Typography variant="h5" className="mb-2">
        Data Tools
      </Typography>
      <Typography variant="body2" className="mb-4 text-gray-600">
        Run bulk content operations against the Content Engine. Use tabs to switch between destinations and guides.
      </Typography>

      <Paper className="p-4 mb-6">
        <Typography variant="subtitle2" className="font-semibold mb-2">
          Site chrome & page copy
        </Typography>
        <Typography variant="body2" className="mb-3 text-gray-600">
          Seed default menus, page titles, How It Works, 404, search UI, and community stories into Firestore
          (skips existing docs; fills missing page_copy fields). After seeding, use Translate to fill DE/FR/IT
          where English was duplicated. Nav URLs stay unprefixed in CMS; the live site localizes hubs under
          /en/, /de/, /fr/, /it/.
        </Typography>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button
            variant="outlined"
            color="primary"
            size="small"
            disabled={seedingSite}
            onClick={() => void seedSiteContent()}
          >
            {seedingSite ? "Seeding…" : "Seed site content"}
          </Button>
        </div>
        <Typography variant="body2" className="mb-2 text-gray-600">
          Site Chrome (document <code>main</code>)
        </Typography>
        <div className="flex flex-wrap gap-2 mb-3">
          <Button
            variant="outlined"
            size="small"
            disabled={translatingChrome}
            onClick={() =>
              void runSiteTranslate("/translate-site-chrome", "main", "force", setTranslatingChrome)
            }
          >
            {translatingChrome ? "Translating…" : "Translate all"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={translatingChrome}
            onClick={() =>
              void runSiteTranslate("/translate-site-chrome", "main", "fill_empty", setTranslatingChrome)
            }
          >
            Fill untranslated
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={translatingChrome}
            onClick={() =>
              void runSiteTranslate("/translate-site-chrome", "main", "stale_only", setTranslatingChrome)
            }
          >
            Re-translate stale
          </Button>
        </div>
        <Typography variant="body2" className="mb-2 text-gray-600">
          Page Copy — translate each document from its entity actions, or use shortcuts:
        </Typography>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outlined"
            size="small"
            disabled={translatingPageCopy}
            onClick={() =>
              void runSiteTranslate("/translate-page-copy", "home", "force", setTranslatingPageCopy)
            }
          >
            {translatingPageCopy ? "Translating…" : "Translate home"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={translatingPageCopy}
            onClick={() =>
              void runSiteTranslate("/translate-page-copy", "home", "fill_empty", setTranslatingPageCopy)
            }
          >
            Fill home untranslated
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={translatingPageCopy}
            onClick={() =>
              void runSiteTranslate("/translate-page-copy", "not_found", "fill_empty", setTranslatingPageCopy)
            }
          >
            Fill 404 untranslated
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={translatingPageCopy}
            onClick={() =>
              void runSiteTranslate("/translate-page-copy", "search", "fill_empty", setTranslatingPageCopy)
            }
          >
            Fill search untranslated
          </Button>
        </div>
      </Paper>

      <div className="flex flex-wrap gap-2 mb-4" role="tablist" aria-label="Data tools scope">
        <Button
          variant={tab === "destinations" ? "filled" : "outlined"}
          size="small"
          color="primary"
          onClick={() => setTab("destinations")}
          role="tab"
          aria-selected={tab === "destinations"}
        >
          Destinations
        </Button>
        <Button
          variant={tab === "guides" ? "filled" : "outlined"}
          size="small"
          color="primary"
          onClick={() => setTab("guides")}
          role="tab"
          aria-selected={tab === "guides"}
        >
          Guides
        </Button>
      </div>

      <Paper className="p-4 mb-6">
        <Typography variant="subtitle2" className="font-semibold mb-2">
          Batch settings
        </Typography>
        <BatchDelayField valueSec={batchDelaySec} onChangeSec={setBatchDelaySec} />
      </Paper>

      <div role="tabpanel" hidden={tab !== "destinations"}>
        {tab === "destinations" && (
          <DestinationDataTools baseUrl={baseUrl} getToken={getToken} batchDelaySec={batchDelaySec} />
        )}
      </div>
      <div role="tabpanel" hidden={tab !== "guides"}>
        {tab === "guides" && <GuideDataTools baseUrl={baseUrl} getToken={getToken} batchDelaySec={batchDelaySec} />}
      </div>
    </div>
  );
}
